import React, { useMemo } from "react";
import {
  Stack,
  Heading,
  Text,
  RadioGroup,
  Radio,
  Alert,
  TextLink,
  Divider,
  Box,
  Button,
} from "@origin-digital/ods-core";
import { useFormikContext } from "formik";
import { EICFormData } from "./types";
import PostalAddressFields from "./PostalAddressFields";
import POBoxFields from "./POBoxFields";
import LifeSupportFields from "./LifeSupportFields";
import { useFormValidation } from "./useFormValidation";
import { getIsElectricityAndGas, getIsGasLineOnly } from "./helper";
import { poBoxFields, postalAddressFields } from "./EICPublicForm";

interface EICFormPageThreeProps {
  onBack: () => void;
  onContinue: () => void;
}

const EICFormPageThree: React.FC<EICFormPageThreeProps> = ({
  onBack,
  onContinue,
}) => {
  const { values, setValues, setFieldValue, setErrors, errors } =
    useFormikContext<EICFormData>();

  const hasElectricityAndGas = useMemo(
    () => getIsElectricityAndGas(values.connectionTypes),
    [values.connectionTypes]
  );

  const hasOnlyGasLine = useMemo(
    () => getIsGasLineOnly(values.connectionTypes),
    [values.connectionTypes]
  );

  const { isValidating, handleContinueClick } = useFormValidation(onContinue);

  const handlePostalAddressTypeChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newValue = e.target.value;

    // Define field updates based on the new selection
    let fieldsToUpdate: Partial<EICFormData> = {
      postalAddressType: newValue as any,
    };

    if (newValue === "postalAddress") {
      // Clear PO Box fields
      fieldsToUpdate = {
        ...fieldsToUpdate,
        ...poBoxFields,
      };
    } else if (newValue === "poBox") {
      // Clear postal address fields
      fieldsToUpdate = {
        ...fieldsToUpdate,
        ...postalAddressFields,
      };
    } else if (newValue === "premiseAddress") {
      // Clear both postal address and PO Box fields
      fieldsToUpdate = {
        ...fieldsToUpdate,
        ...postalAddressFields,
        ...poBoxFields,
      };
    }

    // Update all fields in a single operation
    setValues({
      ...values,
      ...fieldsToUpdate,
    });
    setErrors({});
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await handleContinueClick();
  };

  return (
    <form onSubmit={handleSubmit}>
      <Stack space={["large", "xlarge"]}>
        {/* Content */}
        <Stack>
          <Heading variant="h3">Additional details</Heading>
          <Text>
            We just need a few more details from you to setup your new account.
          </Text>
        </Stack>
        {/* Content */}

        {/* Bills and letters */}
        <Stack space={["large", "xlarge"]}>
          <Heading variant="h4">Your bills and letters</Heading>

          <Stack space="medium">
            <RadioGroup
              label="Where would you like your bills and information sent?"
              name="billDeliveryMethod"
              value={values.billDeliveryMethod}
              onChange={(e) =>
                setFieldValue("billDeliveryMethod", e.target.value)
              }
            >
              <Radio
                id="post"
                label="Post"
                value="post"
                helperText={
                  values.billDeliveryMethod === "post" ? (
                    <Text variant="body-small" tone="neutralLight">
                      A paper fee of $1.75 per bill incl. GST{" "}
                      <TextLink
                        href="https://www.originenergy.com.au/pricing/additional-charges/paper-bill-fee/"
                        target="_blank"
                      >
                        may apply
                      </TextLink>{" "}
                      if you choose bills by post.
                    </Text>
                  ) : undefined
                }
              />
              <Radio
                id="email"
                label="Email"
                value="email"
                helperText={
                  values.billDeliveryMethod === "email" && values.email
                    ? values.email
                    : undefined
                }
              />
            </RadioGroup>

            <Alert>
              <Text>
                Even with{" "}
                {values.billDeliveryMethod === "post" ? "post" : "email"}{" "}
                selected, we may sometimes contact you by{" "}
                {values.billDeliveryMethod === "post" ? "email" : "post"} or
                SMS. If this isn't safe for you, call us on{" "}
                <TextLink href="tel:1300 137 932">1300 137 932</TextLink> before
                continuing.{" "}
                {values.billDeliveryMethod === "email" &&
                  "If your email is used by another Origin account, you'll get bills and notices by post for this account."}
              </Text>
            </Alert>
          </Stack>

          <RadioGroup
            label="What would you like to use as your postal address?"
            name="postalAddressType"
            value={values.postalAddressType}
            onChange={handlePostalAddressTypeChange}
          >
            <Radio
              id="premise-address"
              label="Use the premise address"
              value="premiseAddress"
              helperText={values.address}
            />
            <Radio
              id="postal-address"
              label="Add a postal address"
              value="postalAddress"
            />
            <Radio id="po-box" label="Add a PO Box" value="poBox" />
          </RadioGroup>
          {values.postalAddressType === "postalAddress" && (
            <PostalAddressFields />
          )}
          {values.postalAddressType === "poBox" && <POBoxFields />}
        </Stack>
        {/* End Bills and letters */}

        {/* Life support */}
        {hasOnlyGasLine ? null : (
          <LifeSupportFields
            values={values}
            errors={errors}
            setFieldValue={setFieldValue}
            hasElectricityAndGas={hasElectricityAndGas}
          />
        )}
        {/* End Life support */}

        <Divider />

        {/* Actions  */}
        <Box display="flex" justifyContent="space-between">
          <Button variant="outlined" onClick={onBack} type="button">
            Back
          </Button>
          <Button onClick={handleContinueClick} spinner={isValidating}>
            Continue
          </Button>
        </Box>
        {/* End Actions */}
      </Stack>
    </form>
  );
};

export default EICFormPageThree;
